// The running timer: a pill in the app shell, start/stop buttons on tasks and rows, and the entry editor that opens
// after stopping. The timer itself lives on the server (`/me/timer`), so it survives reloads and shows in every tab.
import { createContext, useContext, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useStartTimer, useStopTimer, useTimeMutation, useTimer } from "@/api/sprints";
import type { TimeEntry } from "@/api/types/TimeEntry";
import { Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { useToast } from "@/components/Toast";
import { Button, cx, Field, Input, Textarea } from "@/components/ui";
import { formatMinutes, parseDuration } from "@/lib/sprints";
import { projectOfKey } from "@/lib/taskLinks";
import { taskUrl } from "@/lib/work";

/** Stopping a timer anywhere opens its saved entry for adjusting; the shell owns that dialog. */
const TimerContext = createContext<{ stop: () => void; stopping: boolean } | null>(null);

export function TimerProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const stopTimer = useStopTimer();
  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const stop = () =>
    stopTimer.mutate(undefined, {
      onSuccess: (e) => e && setEntry(e),
      onError: (e) => toast.error(e),
    });
  return (
    <TimerContext.Provider value={{ stop, stopping: stopTimer.isPending }}>
      {children}
      {entry && <TimeEntryDialog projectKey={projectOfKey(entry.task_key)} taskKey={entry.task_key} entry={entry} stopped onClose={() => setEntry(null)} />}
    </TimerContext.Provider>
  );
}

function useTimerControls() {
  const ctx = useContext(TimerContext);
  const stopTimer = useStopTimer();
  // Outside the shell (no provider) stopping still saves the entry, just without the editor.
  return ctx ?? { stop: () => stopTimer.mutate(), stopping: stopTimer.isPending };
}

/** "1:02:05" since `since`, ticking every second. */
function useElapsed(since: string | undefined): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [since]);
  if (!since) return "";
  const s = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** The running timer in the shell: elapsed time, its task, stop. `compact` is the icon-only form for narrow screens. */
export function TimerPill({ compact }: { compact?: boolean }) {
  const { t } = useTranslation();
  const timer = useTimer();
  const { stop, stopping } = useTimerControls();
  const running = timer.data ?? null;
  const elapsed = useElapsed(running?.started_at);
  if (!running) return null;
  const label = t("timer.running", { key: running.task.key, time: elapsed });
  if (compact)
    return (
      <Link
        to={taskUrl(running.task)}
        title={label}
        aria-label={label}
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 font-mono text-xs font-semibold text-accent no-underline hover:bg-nav-active hover:no-underline"
      >
        <span className="size-2 animate-pulse rounded-full bg-fail-fg" aria-hidden="true" />
        <Icon name="timer" size={16} />
      </Link>
    );
  return (
    <div role="status" aria-label={label} className="flex items-center gap-2 rounded-[var(--radius-ctl)] border border-accent/40 bg-accent-soft px-2 py-1.5 text-sm">
      <span className="size-2 shrink-0 animate-pulse rounded-full bg-fail-fg" aria-hidden="true" />
      <span className="font-mono font-semibold tabular-nums text-accent">{elapsed}</span>
      <Link to={taskUrl(running.task)} title={running.task.title} className="min-w-0 flex-1 truncate font-mono text-xs text-text-2">
        {running.task.key}
      </Link>
      <button
        type="button"
        onClick={stop}
        disabled={stopping}
        aria-label={t("timer.stop")}
        title={t("timer.stop")}
        className="inline-flex size-6 items-center justify-center rounded-md text-fail-fg hover:bg-surface disabled:opacity-50"
      >
        <Icon name="stop" size={14} />
      </button>
    </div>
  );
}

/** Start or stop the timer on one task. `size="sm"` is the inline form for list rows. */
export function TimerButton({ taskKey, size = "md", label }: { taskKey: string; size?: "sm" | "md"; label?: boolean }) {
  const { t } = useTranslation();
  const toast = useToast();
  const timer = useTimer();
  const start = useStartTimer();
  const { stop, stopping } = useTimerControls();
  const runningHere = timer.data?.task.key === taskKey.toUpperCase();
  const elapsed = useElapsed(runningHere ? timer.data?.started_at : undefined);
  const busy = start.isPending || stopping;
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (runningHere) stop();
    else start.mutate(taskKey, { onError: (err) => toast.error(err) });
  };
  const text = runningHere ? t("timer.stopOn", { key: taskKey }) : t("timer.startOn", { key: taskKey });
  if (size === "sm")
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        aria-label={text}
        title={text}
        className={cx(
          "inline-flex h-6 items-center gap-1 rounded-md px-1 font-mono text-xs tabular-nums disabled:opacity-50",
          runningHere ? "bg-fail-bg font-semibold text-fail-fg" : "text-muted hover:bg-surface-2 hover:text-text",
        )}
      >
        <Icon name={runningHere ? "stop" : "play"} size={12} />
        {runningHere && elapsed}
      </button>
    );
  return (
    <Button size="sm" variant={runningHere ? "danger" : "secondary"} icon={runningHere ? "stop" : "play"} onClick={onClick} loading={busy} aria-label={label ? undefined : text} title={text}>
      {runningHere ? <span className="font-mono tabular-nums">{elapsed}</span> : label ? t("timer.start") : null}
    </Button>
  );
}

/**
 * Log time on a task, or edit an entry (`entry`). After a timer stops (`stopped`), the saved entry opens here so the
 * person can adjust it; closing keeps it as saved.
 */
export function TimeEntryDialog({
  projectKey,
  taskKey,
  entry,
  stopped,
  onClose,
}: {
  projectKey: string;
  taskKey: string;
  entry?: TimeEntry;
  stopped?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const mutate = useTimeMutation(projectKey, taskKey);
  const [duration, setDuration] = useState(entry ? formatMinutes(entry.minutes) : "");
  const [date, setDate] = useState(entry?.spent_on ?? "");
  const [note, setNote] = useState(entry?.note ?? "");
  const [touched, setTouched] = useState(false);
  const minutes = parseDuration(duration);
  const error = minutes == null ? t("timer.durationInvalid") : minutes < 1 || minutes > 24 * 60 ? t("timer.durationRange") : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (error || minutes == null) return;
    const done = {
      onSuccess: () => {
        toast.success(entry ? t("timer.entrySaved") : t("timer.logged", { time: formatMinutes(minutes) }));
        onClose();
      },
      onError: (err: unknown) => toast.error(err),
    };
    if (entry) mutate.mutate({ op: "update", id: entry.id, minutes, spent_on: date || undefined, note: note.trim() }, done);
    else mutate.mutate({ op: "log", minutes, spent_on: date || null, note: note.trim() }, done);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={440}
      title={stopped ? t("timer.stoppedTitle", { key: taskKey }) : entry ? t("timer.editTitle", { key: taskKey }) : t("timer.logTitle", { key: taskKey })}
      footer={
        <>
          <Button onClick={onClose}>{stopped ? t("timer.keep") : t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form="time-entry" loading={mutate.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {stopped && <p className="m-0 text-sm text-muted">{t("timer.stoppedBody")}</p>}
      <form id="time-entry" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("timer.duration")} hint={touched && error ? undefined : t("timer.durationHint")} error={touched ? error : undefined}>
            {(p) => (
              <Input
                {...p}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                onBlur={() => {
                  setTouched(true);
                  if (minutes != null && !error) setDuration(formatMinutes(minutes));
                }}
                placeholder="2h 30m"
                autoComplete="off"
              />
            )}
          </Field>
          <Field label={t("timer.date")} hint={entry ? undefined : t("timer.dateHint")}>
            {(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}
          </Field>
        </div>
        <Field label={t("timer.note")} optional>
          {(p) => <Textarea {...p} value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} />}
        </Field>
      </form>
    </Dialog>
  );
}
